import { type AtendenteDTO, type ConversaDetalheDTO, type OrcamentoDTO } from '@viniko/shared';
import { useQuery } from '@tanstack/react-query';
import { Calculator, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../../componentes/ui';
import { api } from '../../lib/api';
import { copiarTexto } from '../../lib/copiar';
import { useJanelaAberta } from '../../lib/consultas';
import {
  HOT_STAMP,
  ACRILICO,
  ESTAMPARIA,
  TROCA_OLEO,
  colarNaMensagem,
  materialProvavel,
  medidasEmCm,
  produtoProvavel,
  resumoCotacao,
  type OpcoesCotacao,
  type ResultadoCotacao,
} from '../../lib/cotacao';

type Item = OrcamentoDTO['itens'][number];

interface Plano {
  material: string | null;
  produto?: string;
  qtd: number | null;
  largura?: number;
  altura?: number;
  tamanho?: string;
  impressao?: string;
  corte?: string;
}

function montarPlano(i: Item, o: OpcoesCotacao | undefined): Plano {
  const texto = [i.material, i.cores, i.apelido].filter(Boolean).join(' ');
  const material = materialProvavel(texto);
  const medidas = medidasEmCm(i.tamanho);
  const plano: Plano = { material, qtd: i.quantidade };
  if (material === HOT_STAMP) {
    plano.tamanho = o?.hotStamp?.tamanhos.find((t) => {
      const m = medidasEmCm(t);
      return m && medidas && m.largura === medidas.largura && m.altura === medidas.altura;
    });
    plano.impressao = o?.hotStamp?.impressao[0];
    return plano;
  }
  const opcoes = o?.materiais.find((m) => m.nome === material);
  if (medidas) Object.assign(plano, medidas);
  plano.produto = opcoes ? produtoProvavel(texto, opcoes.produtos) : undefined;
  plano.impressao = opcoes?.impressao[0];
  plano.corte = opcoes?.corte[0];
  return plano;
}

function faltando(p: Plano): string | null {
  if (!p.material) return 'o material';
  if (!p.qtd) return 'a quantidade';
  if (p.material === TROCA_OLEO) return 'escolher tipo, medida e modelo da troca de óleo';
  if (p.material === ACRILICO) return 'montar as peças do acrílico';
  if (p.material === ESTAMPARIA) return 'escolher os locais e as cores do silk';
  if (p.material === HOT_STAMP) return p.tamanho ? null : 'um tamanho de hot-stamp da tabela';
  return p.largura && p.altura ? null : 'o tamanho';
}

function linkDe(p: Plano): string {
  const q = new URLSearchParams();
  if (p.material) q.set('material', p.material);
  if (p.produto) q.set('produto', p.produto);
  if (p.qtd) q.set('qtd', String(p.qtd));
  if (p.largura) q.set('largura', String(p.largura));
  if (p.altura) q.set('altura', String(p.altura));
  return `/cotacao?${q}`;
}

export function CalcularItem({
  item,
  conversa: c,
  eu,
  aoColar,
}: {
  item: Item;
  conversa: ConversaDetalheDTO;
  eu: AtendenteDTO;
  aoColar: () => void;
}) {
  const toast = useToast();
  const janelaAbertaAgora = useJanelaAberta();
  const [calculando, setCalculando] = useState(false);
  const opcoes = useQuery({
    queryKey: ['cotacao', 'opcoes'],
    queryFn: () => api<OpcoesCotacao>('/api/cotacao/opcoes'),
    staleTime: 10 * 60_000,
    retry: false,
  });
  const plano = montarPlano(item, opcoes.data);

  async function calcular() {
    const falta = faltando(plano);
    if (falta) {
      toast(`Falta ${falta} pra calcular. Use o botão ao lado pra ajustar na cotação.`, 'erro');
      return;
    }
    setCalculando(true);
    try {
      const r = await api<ResultadoCotacao>('/api/cotacao/calcular', {
        metodo: 'POST',
        corpo: { ...plano, acabamentos: [], frete: 0 },
      });
      const texto = resumoCotacao({
        material: plano.material!,
        produto: plano.produto,
        qtd: plano.qtd!,
        medida: plano.tamanho ?? `${plano.largura} × ${plano.altura} cm`,
        detalhes: [plano.impressao ?? '', plano.corte ?? ''],
        frete: 0,
        resultado: r,
      });
      const podeEscrever =
        c.status === 'atendimento' &&
        c.atendente?.id === eu.id &&
        janelaAbertaAgora(c.ultimaMensagemClienteEm);
      if (podeEscrever) {
        colarNaMensagem(c.id, texto);
        aoColar();
        toast('Cotação colada na mensagem. Confira e envie');
      } else {
        await copiarTexto(texto);
        toast('Copiei a cotação. Assuma a conversa pra poder enviar');
      }
    } catch (e) {
      toast((e as Error).message, 'erro');
    } finally {
      setCalculando(false);
    }
  }

  return (
    <div className="orc-calcular">
      <button className="btn" disabled={calculando} onClick={() => void calcular()}>
        <Calculator /> {calculando ? 'Calculando…' : 'Calcular e colar'}
      </button>
      <Link
        className="icone-btn"
        to={linkDe(plano)}
        title="Ajustar na cotação"
        aria-label="Ajustar na cotação"
      >
        <SlidersHorizontal />
      </Link>
    </div>
  );
}
